// src/Pages/RulesPage/RulesPage.js
import React, { useMemo } from "react";
import "./RulesPage.css";
import logo from "../../assets/logo.webp";
import { useContent, useSettings } from "../../utils/useContent";
import SubscribeForm from "../../Components/SubscribeForm/SubscribeForm";
import SEO from "../../Components/SEO/SEO";

const FALLBACK_HEADER = {
  "rules.title": "Rules & Code of Conduct",
  "rules.intro": "Please review these rules before participating in any ASX league or tournament.",
};

const FALLBACK_SECTIONS = [
  {
    title: "Code of Conduct",
    content_type: "paragraph",
    body_text:
      "Any form of violence, aggression, or abusive language directed toward organizers, referees, or other players will result in immediate disqualification and removal from our leagues or tournaments. ASX reserves the right to take such action at its sole discretion.",
  },
  {
    title: "League Rules",
    content_type: "list",
    list_items: [
      "Players are prohibited to register for more than one team playing in the same division.",
      "The maximum number of players per team is 15.",
    ],
  },
];

function slugify(str) {
  return String(str || "")
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
}

export default function RulesPage() {
  const header = useSettings("rules.", FALLBACK_HEADER);
  const { items: sections } = useContent("rule-sections", FALLBACK_SECTIONS);

  const rendered = useMemo(
    () => sections.map((s) => ({ ...s, _anchor: slugify(s.title) })),
    [sections]
  );

  return (
    <>
      <SEO
        title="Rules & Code of Conduct | All Star Xperience"
        description="Official ASX rules, league regulations, and code of conduct for players participating in All Star Xperience Montreal 7v7 soccer leagues and tournaments."
        keywords="ASX rules, soccer league rules Montreal, 7v7 rules, code of conduct"
      />
      <main className="rules-root">
        <div className="container">
          <header className="rules-header">
            <h1 className="rules-h1">{header["rules.title"]}</h1>
            <p className="rules-intro">{header["rules.intro"]}</p>
          </header>

          {rendered.map((section) => (
            <section
              key={section.id ?? section.title}
              className="rule-card"
              id={section._anchor}
            >
              <h2 className="rule-title">{section.title}</h2>
              {section.content_type === "list" ? (
                <ul className="rule-list">
                  {(section.list_items || []).map((item, i) => (
                    <li key={i}>{item}</li>
                  ))}
                </ul>
              ) : (
                <p className="rule-text">{section.body_text}</p>
              )}
            </section>
          ))}
        </div>
      </main>

      <Footer />
    </>
  );
}

/* ---------- Footer (same as Landing/Leagues, with "Leagues" link) ---------- */
function Footer() {
  const year = new Date().getFullYear();

  const links = [
    { label: "Home", href: "/" },
    { label: "Leagues", href: "/leagues" },
    { label: "Hall of Fame", href: "/hall-of-fame" },
    { label: "Rules", href: "/rules" },
    { label: "About", href: "/about" },
    { label: "Register", href: "/register" },
  ];

  return (
    <footer className="site-footer" role="contentinfo">
      <div className="footer-top container">
        {/* Brand (big logo, no blurb) */}
        <div className="ft-col ft-brand">
          <div className="ft-logo">
            <img
              src={logo}
              alt="All Star Xperience"
              width="240"
              height="240"
              className="ft-logo-img"
              style={{ display: "block", objectFit: "contain" }}
            />
          </div>

          <div className="ft-social">
            <a
              className="ft-social-btn"
              href="https://www.instagram.com/allstarxperience/#"
              target="_blank"
              rel="noreferrer"
              aria-label="Instagram"
              title="Instagram"
            >
              <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
                <path fill="currentColor" d="M7 2h10a5 5 0 0 1 5 5v10a5 5 0 0 1-5 5H7a5 5 0 0 1-5-5V7a5 5 0 0 1 5-5Zm0 2a3 3 0 0 0-3 3v10a3 3 0 0 0 3 3h10a3 3 0 0 0 3-3V7a3 3 0 0 0-3-3H7Zm5 3.5A5.5 5.5 0 1 1 6.5 13 5.5 5.5 0 0 1 12 7.5Zm0 2A3.5 3.5 0 1 0 15.5 13 3.5 3.5 0 0 0 12 9.5Zm6-2.25a1.25 1.25 0 1 1-1.25 1.25A1.25 1.25 0 0 1 18 7.25Z"/>
              </svg>
              <span>Instagram</span>
            </a>

            <a
              className="ft-social-btn"
              href="https://www.facebook.com/allstarxperience"
              target="_blank"
              rel="noreferrer"
              aria-label="Facebook"
              title="Facebook"
            >
              <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
                <path fill="currentColor" d="M13.5 9H16l.5-3h-3V4.5c0-.8.2-1.5 1.5-1.5H16V0h-2c-2.5 0-4 1.5-4 4.2V6H8v3h2v9h3.5V9Z"/>
              </svg>
              <span>Facebook</span>
            </a>
          </div>
        </div>

        {/* Contact */}
        <div className="ft-col" id="contact">
          <h4 className="ft-title">Contact</h4>
          <ul className="ft-contact">
            <li>
              <span className="ft-k">Email</span>
              <a href="mailto:allstarxperience@gmail.com">allstarxperience@gmail.com</a>
            </li>
            <li>
              <span className="ft-k">Phone</span>
              <a href="tel:+14387283726">+1 (438) 728-3726</a>
            </li>
            <li>
              <span className="ft-k">Phone</span>
              <a href="tel:+14385089880">+1 (438) 508-9880</a>
            </li>
            <li>
              <span className="ft-k">City</span>
              <span>Montreal, QC</span>
            </li>
          </ul>
        </div>

        {/* Quick Links */}
        <div className="ft-col explore-col">
          <h4 className="ft-title explore-title">Explore</h4>
          <ul className="ft-links">
            {links.map((l) => (
              <li key={l.label}><a href={l.href}>{l.label}</a></li>
            ))}
          </ul>
        </div>

        {/* Newsletter */}
        <div className="ft-col">
          <h4 className="ft-title">Stay in the Loop</h4>
          <p className="ft-note">News, fixtures, and registration reminders.</p>
          <SubscribeForm />
        </div>
      </div>

      <div className="footer-bottom">
        <div className="container footer-bottom-inner">
          <span>© {year} All Star Xperience. All rights reserved. Developed by Riachiッ.</span>
          <div className="ft-legal-links">
            <a href="/privacy">Privacy</a>
            <a href="/terms">Terms</a>
            <a href="#contact">Contact</a>
          </div>
        </div>
      </div>
    </footer>
  );
}
